import { lazy, Suspense, useEffect, useState } from "react";
import { Pager } from "../../components/Pager";
import { Prikker } from "../../components/Prikker";
import type { Oekt } from "../../state/session";
import { OppsettProduksjon } from "./OppsettProduksjon";
import { OppsettTeknisk } from "./OppsettTeknisk";
import { OppsettInnhold } from "./OppsettInnhold";
/* `state/opptaksdager` og ikke `state/oppsett`: tegneflata vil ha dager
   med DATO, og `oppsett.hentDager` baerer naa ogsaa dager uten — de som
   bare har logg paa seg. En kameraplan hoerer til en planlagt dag. */
import { hentDager, type Opptaksdag } from "../../state/opptaksdager";

/* Lastes foerst naar fanen aapnes. Tegneflata er den tyngste modulen i
   bunten, og de tre andre fanene skal ikke vente paa den. */
const Tegneflate = lazy(() => import("../../kameraplan/Tegneflate")
  .then((m) => ({ default: m.Tegneflate })));

/**
 * Oppsett-siden.
 *
 * ========  TO SIDER PÅ ØVERSTE NIVÅ  ========
 *
 * Brukeren 27.10.2026: «Slik som siden med opplasting, brief, logg [...] er
 * én side, så går man inn og velger om man skal se den siden, eller
 * oppsett-siden som har produksjon, teknisk og innhold som felt.»
 *
 * Arbeidsflata er én side med sine faner. Dette er den andre, med sine
 * tre. Man bytter mellom dem i menyen — ikke med en «Ferdig»-knapp, som
 * var problemet med den forrige løsningen.
 *
 * Hvorfor de ikke deler fanerekke: oppsettet er noe man er innom når man
 * rigger og ellers aldri. Lå de i samme rekke, ville tre faner man bruker i
 * august stått i veien hver dag resten av produksjonen — og «Produksjon»,
 * «Teknisk» og «Innhold» ville dessuten stått rett ved siden av områdene
 * med nøyaktig samme navn.
 *
 * ========  SAMME MATERIALE, SAMME BEVEGELSE  ========
 *
 * Samme `Pager`, samme `.faner`, samme prikker. Den skal kjennes som
 * flata, ikke som en innstillingsdialog — og den som har lært å bla her
 * har lært å bla der.
 */

/*
 * ========  KAMERAPLANEN HOERER HIT  ========
 *
 * Brukeren 02.11.2026: «Vi kan ogsaa flytte kameraplan til oppsetts-siden.
 * Den er det A-foto som bruker.»
 *
 * Det stemmer med delingen: tegneflata er noe man rigger med, ikke noe man
 * blar forbi paa en opptaksdag. Den sto som fane nummer ti i arbeidsflata og
 * laastes nedlastet i en `Suspense` hver gang man bladde forbi.
 *
 * Den staar sist, etter Teknisk, fordi den hoerer til det tekniske — men
 * som en egen fane og ikke et kort: en tegneflate trenger hele hoeyden.
 */
const FELT = [
  { id: "produksjon", navn: "Produksjon" },
  { id: "teknisk", navn: "Teknisk" },
  { id: "innhold", navn: "Innhold" },
  { id: "kameraplan", navn: "Kameraplan" },
] as const;

export function OppsettSide({ oekt }: { readonly oekt: Oekt }): JSX.Element {
  const [index, setIndex] = useState(0);
  /* Tegneflata trenger opptaksdagene: en kameraplan hoerer til én dag. */
  const [dager, setDager] = useState<readonly Opptaksdag[]>([]);
  useEffect(() => {
    void (async () => setDager(await hentDager(oekt.production.id)))();
  }, [oekt.production.id]);

  return (
    <>
      <nav className="faner" aria-label="Oppsett">
        {FELT.map((f, i) => (
          <button
            key={f.id}
            type="button"
            className={`faner__fane${i === index ? " faner__fane--aktiv" : ""}`}
            aria-current={i === index}
            onClick={() => setIndex(i)}
          >
            {f.navn}
          </button>
        ))}
      </nav>

      <Pager index={index} onIndexChange={setIndex}>
        {[
          <OppsettProduksjon key="produksjon" oekt={oekt} />,
          <OppsettTeknisk key="teknisk" oekt={oekt} />,
          <OppsettInnhold key="innhold" oekt={oekt} />,
          <Suspense
            key="kameraplan"
            fallback={<div className="flate"><div className="flate__innhold">
              <p className="synk__melding">Åpner tegneflata …</p>
            </div></div>}
          >
            {/* Tegneflata maa vite naar du forlater den — da spoer den om
                settingen skal lagres. Se kameraplan/Tegneflate.tsx. */}
            <Tegneflate oekt={oekt} aktiv={index === 3} dager={dager} />
          </Suspense>,
        ]}
      </Pager>

      <Prikker
        antall={FELT.length}
        index={index}
        navn={FELT.map((f) => f.navn)}
        onVelg={setIndex}
      />
    </>
  );
}
